import { QuoteLineItemProps } from '@partsbadger/types';

import { isCustomCoatingSelected, isCustomFinishSelected, isCustomMaterialSelected } from './util';

/** Field error as produced by QuoteStore.errorFields (`<field>__<lineItemId>`). */
export interface PartErrorField {
    fieldName: string;
    lineItem: string;
}

export type MissingSpec = 'material' | 'finish' | 'coating';

const SPEC_BY_FIELD: Record<string, MissingSpec> = {
    material: 'material',
    custom_material: 'material',
    finishes: 'finish',
    custom_finish: 'finish',
    coatings: 'coating',
    custom_coating: 'coating',
};

const SPEC_ORDER: Array<MissingSpec> = ['material', 'finish', 'coating'];

/**
 * Specs still missing on any variant of a part, using the same rules as the
 * "Continue" validation (QuoteStore.errorFields), in material/finish/coating order.
 */
export const getMissingSpecs = (
    errorFields: Array<PartErrorField>,
    lineItemIds: Array<number | string>
): Array<MissingSpec> => {
    const ids = new Set(lineItemIds.map(id => String(id)));
    const missing = new Set<MissingSpec>();
    errorFields.forEach(({ fieldName, lineItem }) => {
        if (!ids.has(String(lineItem))) return;
        const spec = SPEC_BY_FIELD[fieldName.slice(0, fieldName.lastIndexOf('__'))];
        if (spec) missing.add(spec);
    });
    return SPEC_ORDER.filter(spec => missing.has(spec));
};

type CatalogEntry = { id: number; name: string };

/**
 * Required-spec errors of one line item, from its saved state. `QuoteStore.errorFields`
 * concatenates these for every item; a part card uses its own so editing one part does
 * not re-render every other part.
 */
export const getLineItemErrorFields = (
    item: QuoteLineItemProps,
    catalogs: { materials?: Array<CatalogEntry>; finishes?: Array<CatalogEntry>; coatings?: Array<CatalogEntry> }
): Array<PartErrorField> => {
    const errors: Array<PartErrorField> = [];
    const add = (field: string) => errors.push({ fieldName: `${field}__${item.id}`, lineItem: String(item.id) });

    if (isCustomMaterialSelected(item, catalogs.materials)) {
        if (!item?.product?.custom_material) add('custom_material');
    } else if (!item?.product?.material && !item?.product?.material_related?.id && !item?.material?.id) {
        add('material');
    }

    if (isCustomFinishSelected(item, catalogs.finishes)) {
        if (!item?.product?.custom_finish) add('custom_finish');
    } else if (item?.product?.finishes?.length === 0 && !item?.product?.finish_related?.id && !item?.finish?.id) {
        add('finishes');
    }

    if (isCustomCoatingSelected(item, catalogs.coatings)) {
        if (!item?.product?.custom_coating) add('custom_coating');
    } else if (item?.product?.coatings?.length === 0 && !item?.product?.coating_related?.id && !item?.coating?.id) {
        add('coatings');
    }

    return errors;
};

/** True when a form field holds a value (a select's `{ key }` or a non-empty text). */
export const hasFormValue = (value: unknown): boolean => {
    if (value == null) return false;
    if (typeof value === 'object') {
        const key = (value as { key?: unknown }).key;
        return key != null && key !== '';
    }
    return String(value).trim() !== '';
};

/**
 * Hide a saved-state error once the user has filled the field: the value is being saved,
 * so "Required" should not linger until the server answers.
 */
export const withoutFilledFields = (
    errors: Array<PartErrorField>,
    formValues: Record<string, unknown>
): Array<PartErrorField> => errors.filter(error => !hasFormValue(formValues[error.fieldName]));
